import { Link } from "react-router";
import type { Lesson } from "../../lib/lessonsApi";
import { Icon } from "../../components/ui/Icon";
import { Sheet } from "../../components/ui/Sheet";
import { cap, shortDate } from "../products/text";
import { lessonStr, sentence } from "./lessonText";

type Call = "agree" | "dispute" | "retire";

const CALLS: [Call, string][] = [
  ["agree", "I agree"],
  ["dispute", "I disagree"],
  ["retire", "Retire it"],
];

const CALL_NOTE: Record<Call, string> = {
  agree: "You agreed. New results still re-check it.",
  dispute: "You disputed it. Plans now treat it as a guess and re-test it before leaning on it.",
  retire: "You retired it. Plans no longer use it; its history stays in the log.",
};

interface Props {
  lesson: Lesson | null;
  busy: boolean;
  onClose: () => void;
  /** Left out for a role that may not make the call. */
  onCall?: (lesson: Lesson, call: Call) => void;
}

/** One lesson in the prototype's sheet: its evidence, how sure, where it applies and came from, what uses it, and the owner's call. */
export function LessonSheet({ lesson: l, busy, onClose, onCall }: Props) {
  const call: Call | null = l?.state === "retired" ? "retire" : (l?.call ?? null);
  const from = l?.learnedIn;
  const log = from ? `/goals/${encodeURIComponent(from.goalId)}/log${from.runId ? `?run=${encodeURIComponent(from.runId)}` : ""}` : null;
  return (
    <Sheet open={l !== null} onClose={onClose} label={l ? `Lesson ${l.code}` : "Lesson"}>
      {l && (
        <>
          <div className="ls-h">
            <span className="lid">{l.code}</span>
            <span className={`str ${lessonStr(l)}`}>{lessonStr(l)}</span>
          </div>
          <h2>{l.claim}</h2>
          <div className="eyebrow">What it is based on</div>
          {l.evidence.map((e, i) => (
            <div className="ev3" key={i}>
              {sentence(e.fact)}
              <span className="ev-src">{e.source}</span>
            </div>
          ))}
          <div className="eyebrow">How sure</div>
          <p className="ls-p">
            <b>{cap(l.strength)}.</b> {l.whyStrength}
          </p>
          <div className="eyebrow">Where it applies</div>
          <p className="ls-p">{l.applies ?? "Not said."}</p>
          <div className="eyebrow">Where it came from</div>
          <p className="ls-p">
            {from?.sentence ? `${from.sentence} · ${shortDate(l.createdAt)}` : shortDate(l.createdAt)}
            {log ? (
              <>
                {" "}
                <Link className="chip link" to={log}>
                  Open in the log
                  <Icon name="right" />
                </Link>
              </>
            ) : (
              " · an older run"
            )}
          </p>
          <div className="eyebrow">Used by</div>
          {l.usedBy.length ? (
            <ul className="bl">
              {l.usedBy.map((u) => (
                <li key={u.goalId}>{u.sentence}</li>
              ))}
            </ul>
          ) : (
            <p className="ls-p none">Nothing yet.</p>
          )}
          {l.state === "accepted" || l.state === "retired" ? (
            <>
              <div className="eyebrow">Your call</div>
              {call && (
                <div className="cnote call">
                  <Icon name="reply" />
                  <span>{CALL_NOTE[call]}</span>
                </div>
              )}
              {l.state === "accepted" && onCall && (
                <div className="seg2">
                  {CALLS.map(([k, label]) => (
                    <button
                      type="button"
                      key={k}
                      className={call === k ? "btn sm" : "btn sm ghost"}
                      aria-pressed={call === k}
                      disabled={busy}
                      onClick={() => call !== k && onCall(l, k)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="cnote top">
              <Icon name="clock" />
              <span>{l.state === "proposed" ? "Waiting for you to accept or reject it on the Lessons page." : "Rejected. Kept in history, never used."}</span>
            </div>
          )}
        </>
      )}
    </Sheet>
  );
}
